import {
    collection,
    query,
    getDocs,
    getDoc,
    addDoc,
    doc,
    onSnapshot,
    where,
  } from 'firebase/firestore';

  import { db } from '../firebase/firebase';
  import { CakeOrder } from "../models/CakeOrder";
import customerService from './customer.service';
import { normalizePhoneForOrder } from '../helpers/phone';
import userService from './user.service';
import {
    writeCreateAudit,
    softDeleteWithAudit,
    writeWithAudit,
    computeOrderDiff,
} from './orderAudit.service';

class CakeOrdersService {
    unsubscribe = null;
    store_id = null; // Initialize store_id as null

    constructor() {
        this.collection = 'cakes';
        this.unsubscribe = null; // Initialize as null
    }

    async createCakeOrder({date, time, firstName, lastName, phoneNumber, cakeSize, filling1, filling2, color, message, image, comments, cashier, createdAt, prePaid, customerId = null}) {
        // Normalize phone number to E.164 format
        let normalizedPhone;
        try {
            normalizedPhone = normalizePhoneForOrder(phoneNumber);
        } catch (error) {
            // Re-throw normalization errors with user-friendly message
            throw new Error(error.message || 'Invalid phone number format. Please correct the phone number.');
        }
        
        // Validate phone number format: must be in the format +1234567890
        if (!normalizedPhone.match(/^\+\d{10,14}$/)) {
            throw new Error('Invalid phone number format. Must be in the format +1234567890');
        }
        
        // Use normalized phone number for the rest of the function
        phoneNumber = normalizedPhone;

        // Find or create customer if customerId not provided
        let finalCustomerId = customerId;
        if (!finalCustomerId) {
            try {
                const customer = await customerService.findOrCreate(phoneNumber, { firstName, lastName });
                finalCustomerId = customer.id;
            } catch (error) {
                console.error('Error finding/creating customer:', error);
                // Continue without customer link if there's an error
            }
        }

        const collectionRef = collection(db, this.collection);
        const store_id = await userService.getUserStoreId(); // Get the store ID for the current user
        const cake = new CakeOrder({id: null, date, time, firstName, lastName, phoneNumber, cakeSize, filling1, filling2, color, message, image, comments, cashier, createdAt, store_id, prePaid, customerId: finalCustomerId});
        const docRef = await addDoc(collectionRef, cake.toJson());
        cake.id = docRef.id;

        // Audit: record creation
        try {
            await writeCreateAudit(this.collection, docRef.id);
        } catch (auditError) {
            console.error('Error writing create audit:', auditError);
        }

        // Record order on customer
        if (finalCustomerId) {
            try {
                await customerService.recordOrder(finalCustomerId);
            } catch (error) {
                console.error('Error recording order on customer:', error);
            }
        }

        return cake;
    }

    async getCakeOrder(cakeId) {
        const { docSnap } = await this._verifyStoreOwnership(cakeId);
        return CakeOrder.fromFirebase(docSnap);
    }

    async fetchCakes() {
        const storeId = await userService.getUserStoreId();
        const collectionRef = collection(db, this.collection);
        const q = query(collectionRef, where("store_id", "==", storeId));
        const querySnapshot = await getDocs(q);
        const cakes = [];
        querySnapshot.forEach((doc) => {
            if (!doc.data().deleted) {
                cakes.push(CakeOrder.fromFirebase(doc));
            }
        });
        return cakes;
    }

    async _verifyStoreOwnership(cakeId) {
        const docRef = doc(db, this.collection, cakeId);
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) {
            throw new Error('Cake order not found');
        }
        const userStoreId = await userService.getUserStoreId();
        const docStoreId = docSnap.data().store_id;
        if (docStoreId && docStoreId !== userStoreId) {
            throw new Error('Access denied: order belongs to a different store');
        }
        return { docRef, docSnap };
    }

    async editCakeOrder(cakeId, differences) {
        const { docSnap } = await this._verifyStoreOwnership(cakeId);
        const currentData = docSnap.data();
        const changes = computeOrderDiff(currentData, differences);
        if (Object.keys(changes).length === 0) return;

        await writeWithAudit(this.collection, cakeId, differences, 'updated', changes);
    }

    async deleteCakeOrder(cakeId) {
        const { docSnap } = await this._verifyStoreOwnership(cakeId);
        await softDeleteWithAudit(this.collection, cakeId, docSnap);
    }

    async toggleReadiness(cakeId, ready) {
        const { docSnap } = await this._verifyStoreOwnership(cakeId);
        const currentData = docSnap.data();
        const newValue = !ready;
        const changes = computeOrderDiff(currentData, { ready: newValue });
        if (Object.keys(changes).length === 0) return;

        await writeWithAudit(this.collection, cakeId, { ready: newValue }, 'updated', changes);
    }

    async toggleImageReadiness(cakeId, imageReady) {
        const { docSnap } = await this._verifyStoreOwnership(cakeId);
        const currentData = docSnap.data();
        const newValue = !imageReady;
        const changes = computeOrderDiff(currentData, { imageReady: newValue });
        if (Object.keys(changes).length === 0) return;

        await writeWithAudit(this.collection, cakeId, { imageReady: newValue }, 'updated', changes);
    }

    async togglePrePaid(cakeId, prePaid) {
        const { docSnap } = await this._verifyStoreOwnership(cakeId);
        const currentData = docSnap.data();
        const newValue = !prePaid;
        const changes = computeOrderDiff(currentData, { prePaid: newValue });
        if (Object.keys(changes).length === 0) return;

        await writeWithAudit(this.collection, cakeId, { prePaid: newValue }, 'updated', changes);
    }   

    async fetchCakesWithListener(all, callback) {
        const storeId = await userService.getUserStoreId();
        const cakeCollectionRef = collection(db, this.collection);
        let q;

        if (!all) {
            let cutoffDate = new Date()
            cutoffDate.setDate(cutoffDate.getDate() - 1);
            cutoffDate = cutoffDate.toISOString().split('T')[0]; // format to YYYY-MM-DD

            console.log(`Fetching cakes for storeId: ${storeId} with cutoff date: ${cutoffDate}`);

            q = query(
                cakeCollectionRef,
                where("store_id", "==", storeId),   
                where("date", ">=", cutoffDate),       
                );
        } else {
              q = query(
                cakeCollectionRef,
                where("store_id", "==", storeId),        
                );
        }

       

        this.unsubscribe = onSnapshot(q, (snapshot) => {
            const cakes = snapshot.docs
                .filter((doc) => !doc.data().deleted)
                .map((doc) => CakeOrder.fromFirebase(doc));

            callback(cakes);
        });
}

    stopListening() {
        if (this.unsubscribe) {
            this.unsubscribe(); // Call the unsubscribe function
            this.unsubscribe = null; // Clear it to avoid accidental reuse
            console.log('Unsubscribed from cake orders listener.');
        } else {
            console.warn('No active listener to unsubscribe.');
        }
    }

}

const cakeService = new CakeOrdersService();
export default cakeService;
